import React, { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/api'
import { buildQueueParams, stateBadge } from '../../utils/catalogReview'
import '../../styles/pages.css'

/**
 * Pick the merge target: the surviving product this duplicate's aliases,
 * evidence, and claims move onto. Search runs server-side (q) over every live
 * product by default (all states, all origins) — the canonical row is often a
 * confirmed catalog import that a narrower default would hide (2026-09-03,
 * operator could not find Boss DS-1). Each result shows its state.
 */
// `searchPath` lets a non-admin surface (the Quote Assessment identity card, Prism
// spec kg_quote_path §8) reuse the picker through its own deals-permission proxy;
// `pickLabel` names the action there ("Use" rather than "Merge").
function MergePickerModal({ sourceProduct, onPick, onClose, searchPath = '/api/graph/review/products', pickLabel = 'Merge', title = null }) {
  const [reviewState, setReviewState] = useState('all')
  const [searchInput, setSearchInput] = useState('')
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const loadResults = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = buildQueueParams({ reviewState, page: 1, pageSize: 20, q })
      const response = await api.get(`${searchPath}?${params}`)
      setResults((response.items || []).filter(
        item => item.product_model_id !== sourceProduct?.product_model_id
      ))
      setTotal(response.total || 0)
    } catch (err) {
      console.error('Failed to search merge targets:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [reviewState, q, sourceProduct?.product_model_id, searchPath])

  useEffect(() => {
    loadResults()
  }, [loadResults])

  return (
    <div
      className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: 'var(--space-4)'
      }}
    >
      <div
        className="modal-content card"
        style={{
          width: '100%',
          maxWidth: 600,
          maxHeight: '80vh',
          overflow: 'auto',
          position: 'relative'
        }}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 'var(--space-4)',
          paddingBottom: 'var(--space-3)',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div>
            <h3 style={{ margin: 0, marginBottom: 4 }}>{title ?? 'Merge into…'}</h3>
            <span className="text-sm text-muted">
              {sourceProduct
                ? `${sourceProduct.display_name} becomes a duplicate of the product you pick. Reversible via Unmerge.`
                : 'Search the catalogue and pick the product this item is.'}
            </span>
          </div>
          <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-end', marginBottom: 'var(--space-4)' }}>
          <div className="filter-group" style={{ width: 160 }}>
            <label className="filter-label">Target state</label>
            <select
              className="form-input"
              value={reviewState}
              onChange={e => setReviewState(e.target.value)}
            >
              <option value="all">All states</option>
              <option value="confirmed">Confirmed</option>
              <option value="auto_created">Needs review</option>
            </select>
          </div>
          <div className="filter-group" style={{ flex: 1 }}>
            <label className="filter-label">Search</label>
            <input
              className="form-input"
              type="text"
              placeholder="Product name…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') setQ(searchInput) }}
              autoFocus
            />
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => setQ(searchInput)}>
            Search
          </button>
        </div>

        {error && (
          <div className="card" style={{ background: 'var(--color-error-light)', padding: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
            <strong>Error:</strong> {error}
          </div>
        )}

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
            <div className="loading-spinner" />
          </div>
        ) : results.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-title">No candidates</div>
            <div className="empty-state-description">
              Try a different search or target state.
            </div>
          </div>
        ) : (
          <table className="table">
            <tbody>
              {results.map(item => (
                <tr key={item.product_model_id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {item.display_name}
                      <span className={stateBadge(item.review_state)} style={{ marginLeft: 'var(--space-2)' }}>
                        {item.review_state}
                      </span>
                    </div>
                    <div className="text-sm text-muted">
                      {item.category || '-'} · {item.evidence_count} obs
                    </div>
                  </td>
                  <td style={{ width: 90, textAlign: 'right' }}>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => onPick(item)}
                    >
                      {pickLabel}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!loading && total > results.length && (
          <div className="text-sm text-muted" style={{ marginTop: 'var(--space-2)' }}>
            Showing first {results.length} of {total} — narrow the search to find the right target.
          </div>
        )}
      </div>
    </div>
  )
}

export default MergePickerModal
